import {
  Box,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
} from '@mui/material';
import { AxiosResponse, Method } from 'axios';
import { Formik } from 'formik';
import { OptionsObject, SnackbarKey } from 'notistack';
import React, { useMemo } from 'react';
import { UseMutationResult } from 'react-query';
import { SetterOrUpdater, useRecoilValueLoadable } from 'recoil';
import * as Yup from 'yup';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
} from '../../../common/formik-material-ui';
import { IUserAtom } from '../../../recoil/atoms/user';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { IAlertWithProgressBar } from '../notifications/SnackbarProviderWithStyles';
import { ModalWithStyles } from './ModalWithStyles';

const validationSchema = Yup.object({
  exportName: Yup.string().required('Required').max(300, 'Char limit 300'),
  pageToExport: Yup.string().required('Required'),
  frequency: Yup.string().required('Required'),
  powerlistId: Yup.string().when('pageToExport', {
    is: (page) => page === 'powerlist-contacts',
    then: Yup.string().required('Required'),
  }),
  recipients: Yup.array()
    .required('Required')
    .transform(function (value, originalValue) {
      if (this.isType(value) && value !== null) {
        return value;
      }
      return originalValue ? originalValue.split(/[\s,]+/) : [];
    })
    .of(Yup.string().email(({ value }) => `${value} is not a valid email`)),
});

const ScheduledExportModal = (props: {
  show: boolean;
  handleClose: () => void;
  editID: number | null;
  userAtomContents: IUserAtom;
  mutation: UseMutationResult<
    AxiosResponse<any>,
    unknown,
    {
      endpoint: string;
      payload?:
        | {
            [key: string]: any;
          }
        | undefined;
      method?: Method | undefined;
      omitBusinessIdAndApikey?: boolean | undefined;
    },
    unknown
  >;
  reports: any;
  enqueueSnackbarWithStyles: (
    alertComponentProps: IAlertWithProgressBar,
    snackbarProps?: OptionsObject | undefined
  ) => SnackbarKey;
  setRefetchToggleForScheduledReports: SetterOrUpdater<any>;
}) => {
  const {
    editID,
    userAtomContents,
    reports,
    enqueueSnackbarWithStyles,
    setRefetchToggleForScheduledReports,
  } = props;
  React.useEffect(() => {
    setRefreshToggleForScheduledReport({});
  }, []);
  const getScheduledReportEndpoint = () =>
    userAtomContents.businessid
      ? endpointEnum.GET_SCHEDULED_REPORT.replace(
          '{bid}',
          userAtomContents.businessid.toString()
        ).replace(`{scheduledReportId}`, editID?.toString() as string)
      : '';

  const getEditEndpoint = () =>
    userAtomContents.businessid
      ? endpointEnum.UPDATE_SCHEDULED_REPORT.replace(
          '{bid}',
          userAtomContents.businessid.toString()
        ).replace(`{scheduledReportId}`, editID?.toString() as string)
      : '';

  const [
    _,
    scheduledReportLoadable,
    setRefreshToggleForScheduledReport,
  ] = makeApiRequestCached(
    {
      endpoint: getScheduledReportEndpoint(),
      stage: 'itn',
      method: 'GET',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      payloadJsonString: JSON.stringify({
        apikey: userAtomContents?.apikey,
        businessid: userAtomContents?.businessid,
        businessId: userAtomContents?.businessid,
      }),
      waitForPayload: !props?.editID,
    },
    (loadable, cached) => loadable.contents?.response?.data ?? cached.current
  );

  const powerlistReportLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: `${endpointEnum.GET_BUSINESS_POWERLISTS}`,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: 'v1/getBusinessPowerlists',
      }),
    })
  );
  const powerlists = useMemo(
    () =>
      (powerlistReportLoadable?.contents?.response as AxiosResponse)?.data
        ?.powerlists,
    [powerlistReportLoadable]
  );

  const initialValues = useMemo(() => {
    return {
      exportName: scheduledReportLoadable?.contents?.response?.data?.name ?? '',
      active: scheduledReportLoadable?.contents?.response?.data?.active !== 0,
      pageToExport:
        scheduledReportLoadable?.contents?.response?.data?.kixieReportName &&
        scheduledReportLoadable?.contents?.response?.data?.kixieReportName !==
          null
          ? reports.find(
              (r) =>
                r?.value ===
                scheduledReportLoadable?.contents?.response?.data
                  ?.kixieReportName
            )?.value
          : '',
      frequency:
        scheduledReportLoadable?.contents?.response?.data?.frequency?.toLowerCase() ??
        '',
      recipients: scheduledReportLoadable?.contents?.response?.data?.emailList
        ? JSON.parse(
            scheduledReportLoadable?.contents?.response?.data?.emailList
          )
        : [],
      powerlistId:
        scheduledReportLoadable?.contents?.response?.data?.powerlistId ?? '',
    };
  }, [scheduledReportLoadable, props?.show]);

  const getCreateEndpoint = () =>
    props.userAtomContents.businessid
      ? endpointEnum.ADD_SCHEDULED_REPORT.replace(
          '{bid}',
          props.userAtomContents.businessid.toString()
        )
      : '';
  return (
    <Formik
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={async (values, actions) => {
        try {
          let data = {
            businessId: props.userAtomContents?.business?.businessid as any,
            apikey: props.userAtomContents?.apikey,
            reportType: 'scheduled',
            active: values?.active ? 1 : 0,
            name: values?.exportName,
            frequency: values?.frequency,
            kixieReportName: values?.pageToExport,
            emailList: !Array.isArray(values?.recipients)
              ? values?.recipients?.split(',')?.map((r) => r.trim())
              : values?.recipients,
            createdByUserId: !editID
              ? props.userAtomContents?.userid
              : scheduledReportLoadable?.contents?.response?.data
                  ?.createdByUserId,
            timezone: props.userAtomContents?.business?.timezone_php,
            powerlistId: values?.powerlistId !== '' ? values.powerlistId : null,
          } as any;
          if (editID) {
            data.scheduledReportId = Number(editID);
            data = {
              ...data,
              lastExportDate:
                scheduledReportLoadable?.contents?.response?.data
                  ?.lastExportDate,
              lastExportURL:
                scheduledReportLoadable?.contents?.response?.data
                  ?.lastExportURL,
              lastExportStatus:
                scheduledReportLoadable?.contents?.response?.data
                  ?.lastExportStatus,
              nextExportDate:
                scheduledReportLoadable?.contents?.response?.data
                  ?.nextExportDate,
            };
            if (
              values?.frequency?.toLowerCase() !==
              scheduledReportLoadable?.contents?.response?.data?.frequency?.toLowerCase()
            ) {
              delete data.nextExportDate;
            }
          }
          const response = await props.mutation.mutateAsync({
            endpoint: editID ? getEditEndpoint() : getCreateEndpoint(),
            payload: data,
            domain: process.env.VITE_APP_CDK_DOMAIN,
            stage: 'itn',
            method: editID ? 'PUT' : 'POST',
          });
          if (response?.status === 200 || response?.status === 201) {
            enqueueSnackbarWithStyles({
              message: `Success ${
                editID ? 'updating' : 'creating'
              } scheduled report`,
              alertProps: { severity: 'success' },
            });
            setRefreshToggleForScheduledReport({});
            setRefetchToggleForScheduledReports({});
            props.handleClose();
          }
        } catch (err) {
          props.enqueueSnackbarWithStyles({
            message: `Error updating scheduled report active status`,
            alertProps: { severity: 'error' },
          });
          setRefreshToggleForScheduledReport({});
          props.handleClose();
        }
        actions.resetForm();
      }}
      initialValues={initialValues}
    >
      {(formik) => (
        <ModalWithStyles
          onSubmit={() => {
            formik.handleSubmit();
          }}
          title="Scheduled Report Options"
          aria-labelledby="example-modal-sizes-title-lg"
          show={props.show}
          onHide={() => {
            props.handleClose();
            formik.resetForm({});
          }}
          isSubmitting={formik.isSubmitting}
          isLoading={
            formik.isSubmitting || scheduledReportLoadable?.state === 'loading'
          }
          size="sm"
          disableSubmit={!formik.isValid || formik.isSubmitting}
        >
          <Box>
            <LabelledOutline label="Export Settings">
              <Grid container>
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                  <FormikTextField
                    name="exportName"
                    formik={formik}
                    fullWidth
                    label="Export Name"
                    size="small"
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      width: '100%',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <FormikSwitch
                          name="active"
                          formik={formik}
                          color="primary"
                          type="checkbox"
                          handleChange={() => {
                            if (formik.values.active) {
                              formik.setFieldValue('active', false);
                            } else {
                              formik.setFieldValue('active', true);
                            }
                          }}
                        />
                      }
                      label="Active"
                    />
                  </div>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                  <FormikSelect
                    name="pageToExport"
                    formik={formik}
                    fullWidth
                    label="Page to Export"
                    size="small"
                    variant="outlined"
                    required
                  >
                    {reports.map((r, i) => (
                      <MenuItem key={i} value={r.value}>
                        {r.name}
                      </MenuItem>
                    ))}
                  </FormikSelect>
                </Grid>
              </Grid>
              {formik.values?.pageToExport === 'powerlist-contacts' && (
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                  <FormikSelect
                    name="powerlistId"
                    formik={formik}
                    fullWidth
                    label="Select a PowerList"
                    size="small"
                    variant="outlined"
                    required
                  >
                    {[...(powerlists ?? [])]
                      ?.sort((a, b) => {
                        return a?.name
                          ?.toLowerCase()
                          ?.localeCompare(b?.name?.toLowerCase());
                      })
                      .map((powerlist, index) => (
                        <MenuItem key={index} value={powerlist?.powerlistId}>
                          {powerlist?.name}
                        </MenuItem>
                      ))}
                  </FormikSelect>
                </Grid>
              )}
            </LabelledOutline>
            <LabelledOutline label="Export Frequency">
              <p>
                Select a frequency for this export to be delivered. Range of
                data is dependent on the frequency selected.
              </p>
              <Grid container>
                <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                  <FormikSelect
                    name="frequency"
                    formik={formik}
                    fullWidth
                    label="Choose Frequency"
                    size="small"
                    variant="outlined"
                    required
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </FormikSelect>
                  {formik?.values?.frequency === 'daily' && (
                    <FormHelperText id="component-helper-text">
                      Daily reports include the last 24 hours of data from the
                      time the report is sent. This report will send every day
                      at midnight local time.
                    </FormHelperText>
                  )}
                  {formik?.values?.frequency === 'weekly' && (
                    <FormHelperText id="component-helper-text">
                      Weekly reports include the last 7 days of data from the
                      time the report is sent. This report will send every
                      Sunday at midnight local time.
                    </FormHelperText>
                  )}
                  {formik?.values?.frequency === 'monthly' && (
                    <FormHelperText id="component-helper-text">
                      Monthly reports include the previous month’s data from the
                      time the report is sent. This report will send every last
                      day of the month at midnight local time.
                    </FormHelperText>
                  )}
                </Grid>
              </Grid>
            </LabelledOutline>
            <LabelledOutline label="Export Recipients">
              <p>
                Enter email addresses here. Separate multiple addresses with a
                comma. Each email address with recieve an export of the chosen
                page of the frequency selected above.
              </p>
              <Grid container>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <FormikTextField
                    name="recipients"
                    formik={formik}
                    fullWidth
                    label="Email Addresses"
                    size="small"
                    variant="outlined"
                    required
                  />
                </Grid>
              </Grid>
            </LabelledOutline>
          </Box>
        </ModalWithStyles>
      )}
    </Formik>
  );
};

export default ScheduledExportModal;
